import { toast } from "react-toastify";
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import dayjs from "dayjs";
import { useQuery } from "@tanstack/react-query";
import AppleIcon from "@mui/icons-material/Apple";
import GoogleIcon from "@mui/icons-material/Google";
import {
  getAuth,
  unlink,
  OAuthProvider,
  linkWithPopup,
  GoogleAuthProvider,
  linkWithCredential,
  fetchSignInMethodsForEmail,
} from "firebase/auth";
import firebase from "app/Auth";
import { useDialog } from "app/hook";
import { deleteAsset } from "app/api";
import { RootState, AppDispatch } from "app/store";
import { AppPlatform, Asset, DialogType } from "app/types";
import Constants from "common/constant";
import { btnDelay } from "common/utils";
import { useParentAssetCache } from "common/hook";
import { WarningMessageBox, UserAvatar } from "common/elements";
import i18n from "features/shared/i18n";
import { getUserAssets } from "features/player/api";
import { updateAccount } from "features/account/api";
import { usePlan, PlanType } from "features/subscription";
import {
  ParentConfirmModal,
  AssetsSection,
  AssetTabType,
  AssetListModal,
  AssetPreviewModal,
} from "features/shared";
import {
  UsageSection,
  ProfileSection,
  NicknameEditModal,
  QrLoginApproveModal,
  DeleteAccountModal,
  ChangePasswordModal,
  ParentIconEditModal,
} from "./components";

export const AccountPage = () => {
  const plan = usePlan();
  const navigate = useNavigate();
  const handleDialog = useDialog();
  const dispatch = useDispatch<AppDispatch>();
  const parentsAuth = getAuth(firebase);
  const parent = useSelector((state: RootState) => state.user.parent);
  const { data: assetsData } = useQuery({
    queryKey: ["parent/getUserAssets", parent?.uid],
    queryFn: () => getUserAssets({ uid: parent.uid }),
    enabled: !!parent?.uid,
    staleTime: 5 * 60 * 1000,
  });
  const assets = assetsData?.assets ?? [];
  const { onParentAssetDeleted } = useParentAssetCache();
  const [nicknameModalOpen, setNicknameModalOpen] = useState(false);
  const [signOutConfirm, setSignOutConfirm] = useState(false);
  const [qrLoginOpen, setQrLoginOpen] = useState(false);
  const [deleteAccountOpen, setDeleteAccountOpen] = useState(false);
  const [changePasswordOpen, setChangePasswordOpen] = useState(false);
  const [iconModalOpen, setIconModalOpen] = useState(false);
  // 素材関連
  const [selectedAssetTabs, setSelectedAssetTabs] = useState<Set<AssetTabType>>(
    new Set([AssetTabType.ALBUM, AssetTabType.AI_IMAGE]),
  );
  const [selectedAsset, setSelectedAsset] = useState<Asset | null>(null);
  const [assetListOpen, setAssetListOpen] = useState(false);
  // SNS連携
  const [apple, setApple] = useState(false);
  const [google, setGoogle] = useState(false);
  const [linkError, setLinkError] = useState(false);
  const nonce = Math.random().toString(36).substring(2, 10);
  // Android/AmazonアプリではApple連携を利用できないため非表示
  const platform = window.parentsAppPlatform || AppPlatform.Web;
  const hideAppleLink =
    platform === AppPlatform.Android || platform === AppPlatform.Amazon;

  // SNS連携状態の取得
  useEffect(() => {
    parentsAuth.currentUser?.providerData.map((provider) => {
      fetchSignInMethodsForEmail(parentsAuth, provider.email).then(
        (methods) => {
          if (provider.providerId === "apple.com" && methods.length > 0) {
            setApple(true);
          }
          if (provider.providerId === "google.com" && methods.length > 0) {
            setGoogle(true);
          }
        },
      );
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Apple Sign In初期化 & メッセージリスナー
  useEffect(() => {
    const handleWebAppleSignInSuccess = (event: CustomEvent) => {
      handleFirebaseAppleSignIn(event.detail.authorization.id_token);
    };

    const handleWebAppleSignInFailure = () => {
      handleDialog({ type: DialogType.LOADING, value: false });
    };

    const handleMessage = (message: MessageEvent) => {
      try {
        const { command, data } = JSON.parse(message.data);
        if (command === "signIn") {
          const { method } = data;
          if (method === "apple" && data.idToken) {
            handleFirebaseAppleSignIn(data.idToken);
          } else if (method === "google" && data.idToken) {
            handleFirebaseGoogleSignIn(data.idToken);
          } else if (!data.idToken) {
            handleLinkError(data.result);
          }
        }
      } catch {
        // ignore non-JSON messages
      }
    };

    const initAppleSign = async () => {
      const encoder = new TextEncoder();
      const encodedNonce = encoder.encode(nonce);
      const hashBuffer = await crypto.subtle.digest("SHA-256", encodedNonce);
      const hashArray = Array.from(new Uint8Array(hashBuffer));
      const hashedNonce = hashArray
        .map((b) => b.toString(16).padStart(2, "0"))
        .join("");

      // @ts-expect-error AppleID is a global injected by external Apple script
      AppleID?.auth?.init({
        clientId: "com.inexus.app.services.parents",
        scope: "email",
        redirectURI: Constants.publicUrl,
        state: "parents",
        nonce: hashedNonce,
        usePopup: true,
      });
    };

    if ("ReactNativeWebView" in window) {
      if (window.parentsAppPlatform === "ios") {
        window.addEventListener("message", handleMessage);
      } else {
        document.addEventListener("message", handleMessage as EventListener);
      }
    } else {
      initAppleSign();
      document.addEventListener(
        "AppleIDSignInOnSuccess",
        handleWebAppleSignInSuccess as EventListener,
      );
      document.addEventListener(
        "AppleIDSignInOnFailure",
        handleWebAppleSignInFailure as EventListener,
      );
    }

    return () => {
      if ("ReactNativeWebView" in window) {
        if (window.parentsAppPlatform === "ios") {
          window.removeEventListener("message", handleMessage);
        } else {
          document.removeEventListener(
            "message",
            handleMessage as EventListener,
          );
        }
      } else {
        document.removeEventListener(
          "AppleIDSignInOnSuccess",
          handleWebAppleSignInSuccess as EventListener,
        );
        document.removeEventListener(
          "AppleIDSignInOnFailure",
          handleWebAppleSignInFailure as EventListener,
        );
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleLinkError = (error: { code?: string }) => {
    if (
      error?.code === "auth/credential-already-in-use" ||
      error?.code === "auth/provider-already-linked"
    ) {
      setLinkError(true);
    }
    handleDialog({ type: DialogType.LOADING, value: false });
    console.error(`link error: ${JSON.stringify(error)}`);
  };

  const handleFirebaseGoogleSignIn = async (idToken: string) => {
    const credential = GoogleAuthProvider.credential(idToken);
    linkWithCredential(parentsAuth.currentUser!, credential)
      .then(() => {
        setGoogle(true);
        handleDialog({ type: DialogType.LOADING, value: false });
      })
      .catch(handleLinkError);
  };

  const handleFirebaseAppleSignIn = async (idToken: string) => {
    const provider = new OAuthProvider("apple.com");
    provider.addScope("email");
    provider.addScope("name");
    const credential = provider.credential({
      idToken: idToken,
      rawNonce: nonce,
    });
    linkWithCredential(parentsAuth.currentUser!, credential)
      .then(() => {
        setApple(true);
        handleDialog({ type: DialogType.LOADING, value: false });
      })
      .catch(handleLinkError);
  };

  const handleLinkApple = async () => {
    if (apple) {
      // 解除
      handleDialog({ type: DialogType.LOADING, value: true });
      unlink(parentsAuth.currentUser!, "apple.com")
        .then(() => {
          setApple(false);
        })
        .catch((error) => {
          console.error(error);
          setLinkError(true);
        })
        .finally(() =>
          handleDialog({ type: DialogType.LOADING, value: false }),
        );
    } else {
      // 連携
      if ("ReactNativeWebView" in window) {
        handleDialog({ type: DialogType.LOADING, value: true });
        window.ReactNativeWebView?.postMessage(
          JSON.stringify({
            command: "signIn",
            data: { method: "apple" },
          }),
        );
      } else {
        // @ts-expect-error AppleID is a global injected by external Apple script
        await AppleID?.auth?.signIn();
      }
    }
  };

  const handleLinkGoogle = () => {
    if (google) {
      // 解除
      handleDialog({ type: DialogType.LOADING, value: true });
      unlink(parentsAuth.currentUser!, "google.com")
        .then(() => {
          setGoogle(false);
        })
        .catch((error) => {
          console.error(error);
        })
        .finally(() =>
          handleDialog({ type: DialogType.LOADING, value: false }),
        );
    } else {
      // 連携
      if ("ReactNativeWebView" in window) {
        handleDialog({ type: DialogType.LOADING, value: true });
        window.ReactNativeWebView.postMessage(
          JSON.stringify({
            command: "signIn",
            data: { method: "google" },
          }),
        );
      } else {
        // Web: linkWithPopup
        handleDialog({ type: DialogType.LOADING, value: true });
        parentsAuth.languageCode = "ja";
        const provider = new GoogleAuthProvider();
        provider.addScope("email");
        linkWithPopup(parentsAuth.currentUser!, provider)
          .then(() => {
            setGoogle(true);
          })
          .catch((error) => {
            console.error(error);
            if (
              error.code === "auth/credential-already-in-use" ||
              error.code === "auth/provider-already-linked"
            ) {
              setLinkError(true);
            }
          })
          .finally(() =>
            handleDialog({ type: DialogType.LOADING, value: false }),
          );
      }
    }
  };

  const handleCloseLinkError = () => {
    setLinkError(false);
  };

  const handleChangePassword = () => {
    btnDelay(() => setChangePasswordOpen(true));
  };

  const handleOpenQrLogin = () => {
    btnDelay(() => setQrLoginOpen(true));
  };

  const handleOpenLogout = () => {
    btnDelay(() => setSignOutConfirm(true));
  };

  const handleLogout = async () => {
    setSignOutConfirm(false);
    btnDelay(async () => {
      handleDialog({ type: DialogType.LOADING, value: true });
      try {
        await parentsAuth.signOut();
        handleDialog({ type: DialogType.LOADING, value: false });
        navigate("/");
      } catch (error) {
        console.error(error);
        handleDialog({ type: DialogType.LOADING, value: false });
        handleDialog({ type: DialogType.ERROR_NETWORK, value: true });
      }
    });
  };

  const handleCloseLogoutConfirm = () => {
    btnDelay(() => setSignOutConfirm(false));
  };

  const handleDeleteAccount = () => {
    btnDelay(() => setDeleteAccountOpen(true));
  };

  const handleOpenNicknameModal = () => {
    btnDelay(() => setNicknameModalOpen(true));
  };

  const handleOpenIconModal = () => {
    btnDelay(() => setIconModalOpen(true));
  };

  const handleToggleAssetTab = (tab: AssetTabType) => {
    setSelectedAssetTabs((prev) => {
      const newTabs = new Set(prev);
      if (newTabs.has(tab)) {
        newTabs.delete(tab);
      } else {
        newTabs.add(tab);
      }
      return newTabs;
    });
  };

  const handleDeleteAsset = (asset: Asset) => {
    handleDialog({ type: DialogType.LOADING, value: true });
    deleteAsset({ id: asset.id })
      .then(() => {
        onParentAssetDeleted(parent.uid, asset.id, asset.file_size || 0);
        setSelectedAsset(null);
        toast(
          <div>
            <p className="text text-textcolor-black">
              {i18n.t("MSG_TOAST_DELETED_SUCCESS")}
            </p>
          </div>,
        );
      })
      .catch((e) => {
        console.error(e);
        toast(
          <div>
            <p className="text text-textcolor-black">
              {i18n.t("MSG_TOAST_ERROR")}
            </p>
          </div>,
        );
      })
      .finally(() => handleDialog({ type: DialogType.LOADING, value: false }));
  };

  const handleNicknameConfirm = (newName: string) => {
    handleDialog({ type: DialogType.LOADING, value: true });
    dispatch(
      updateAccount({
        uid: parent.uid,
        userInfo: { display_name: newName },
      }),
    )
      .unwrap()
      .then(() => {
        toast(
          <div>
            <p className="text text-textcolor-black">
              {i18n.t("MSG_TOAST_EDIT_SUCCESS")}
            </p>
          </div>,
        );
        setNicknameModalOpen(false);
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => handleDialog({ type: DialogType.LOADING, value: false }));
  };

  if (!parent) return null;

  return (
    <div className="flex-col-el gap-6">
      {/* アカウント情報 */}
      <div className="bg-white rounded-[16px] p-6">
        <div className="flex-row-el items-center gap-2 text-[27px] text-textcolor-black mb-5 pb-3 border-b border-outline">
          <span className="material-symbols-outlined !text-[45px] text-red-100">
            badge
          </span>
          {i18n.t("MSG_PARENT_PAGE_ACCOUNT_INFO")}
        </div>

        {/* アイコン */}
        <div className="flex-row-el items-center py-3 border-b border-outline">
          <div className="w-[250px] text-[24px] text-textcolor-black">
            {i18n.t("MSG_PARENT_PAGE_ICON")}
          </div>
          <div className="flex-1">
            <UserAvatar size={72} icon={parent.icon} />
          </div>
          <div className="flex-row-el gap-2">
            <button
              onClick={handleOpenIconModal}
              className="bg-white border border-gray2-60 text-textcolor-black rounded-full py-2 px-4 text-[21px] cursor-pointer hover:bg-gray2-20 transition-all"
            >
              {i18n.t("MSG_PARENT_PAGE_CHANGE")}
            </button>
          </div>
        </div>

        {/* ニックネーム */}
        <div className="flex-row-el items-center py-3 border-b border-outline">
          <div className="w-[250px] text-[24px] text-textcolor-black">
            {i18n.t("MSG_PARENT_PAGE_NICKNAME")}
          </div>
          <div className="flex-1 text-[24px] text-textcolor-black">
            {parent.display_name || "-"}
          </div>
          <div className="flex-row-el gap-2">
            <button
              onClick={handleOpenNicknameModal}
              className="bg-white border border-gray2-60 text-textcolor-black rounded-full py-2 px-4 text-[21px] cursor-pointer hover:bg-gray2-20 transition-all"
            >
              {i18n.t("MSG_PARENT_PAGE_CHANGE")}
            </button>
          </div>
        </div>

        {/* メールアドレス */}
        <div className="flex-row-el items-center py-3 border-b border-outline">
          <div className="w-[250px] text-[24px] text-textcolor-black">
            {i18n.t("MSG_PARENT_PAGE_EMAIL")}
          </div>
          <div className="flex-1 text-[24px] text-textcolor-black">
            {parent.email || "-"}
          </div>
        </div>

        {/* メール認証 */}
        <div className="flex-row-el items-center py-3 border-b border-outline">
          <div className="w-[250px] text-[24px] text-textcolor-black">
            {i18n.t("MSG_PARENT_PAGE_EMAIL_VERIFIED")}
          </div>
          <div className="flex-1">
            {/* TODO: email_verified field */}
            <span className="inline-flex items-center gap-1 py-1 px-3 bg-green-60/20 text-green-100 rounded-[12px] text-[21px]">
              <span className="material-symbols-outlined text-[24px]">
                check_circle
              </span>
              {i18n.t("MSG_PARENT_PAGE_VERIFIED")}
            </span>
          </div>
        </div>

        {/* アカウント作成日 */}
        <div className="flex-row-el items-center py-3">
          <div className="w-[250px] text-[24px] text-textcolor-black">
            {i18n.t("MSG_PARENT_PAGE_CREATED_AT")}
          </div>
          <div className="flex-1 text-[24px] text-textcolor-black">
            {parent.created_at
              ? dayjs(parent.created_at).format("YYYY-MM-DD HH:mm:ss")
              : "-"}
          </div>
        </div>
      </div>

      {/* 利用状況 */}
      <UsageSection />

      {/* 素材セクション */}
      <AssetsSection
        assets={assets}
        selectedAssetTabs={selectedAssetTabs}
        onSelectAsset={(asset) => setSelectedAsset(asset)}
        onShowMore={() => setAssetListOpen(true)}
        onToggleTab={handleToggleAssetTab}
        showSketchbook={false}
      />

      {/* プロフィール情報 */}
      <ProfileSection />

      {/* アカウント連携 - パスワードログインかつEDUCATIONプラン以外のみ表示 */}
      {parent.provider === "password" && plan !== PlanType.EDUCATION && (
        <div className="bg-white rounded-[16px] p-6">
          <div className="flex-row-el items-center gap-2 text-[27px] text-textcolor-black mb-5 pb-3 border-b border-outline">
            <span className="material-symbols-outlined !text-[45px] text-red-100">
              link
            </span>
            {i18n.t("MSG_PARENT_PAGE_SNS_LINK")}
          </div>

          {/* Google */}
          <div className="flex-row-el items-center gap-3 py-3 px-4 bg-white rounded-[12px] mb-2">
            <div className="w-8 h-8 rounded-full bg-[#ea4335] flex items-center justify-center">
              <GoogleIcon sx={{ fontSize: 20, color: "white" }} />
            </div>
            <div className="flex-col-el flex-1">
              <div className="text-[24px] text-textcolor-black">
                {i18n.t("MSG_PARENT_PAGE_GOOGLE_ACCOUNT")}
              </div>
              {google && (
                <div className="text-[18px] text-gray2-100">
                  {
                    parentsAuth.currentUser?.providerData
                      .filter(
                        (provider) => provider.providerId === "google.com",
                      )
                      .shift()?.email
                  }
                </div>
              )}
            </div>
            {google ? (
              <button
                onClick={handleLinkGoogle}
                className="ml-auto bg-transparent border-2 border-textcolor-black text-textcolor-black rounded-full py-2 px-4 text-[21px] cursor-pointer hover:bg-gray2-100/10 transition-all"
              >
                {i18n.t("MSG_PARENT_PAGE_UNLINK")}
              </button>
            ) : (
              <button
                onClick={handleLinkGoogle}
                className="ml-auto bg-white border border-gray2-60 text-textcolor-black rounded-full py-2 px-4 text-[21px] cursor-pointer hover:bg-gray2-20 transition-all"
              >
                {i18n.t("MSG_PARENT_PAGE_LINK")}
              </button>
            )}
          </div>

          {/* Apple */}
          {!hideAppleLink && (
            <div className="flex-row-el items-center gap-3 py-3 px-4 bg-white rounded-[12px]">
              <div className="w-8 h-8 rounded-full bg-black flex items-center justify-center">
                <AppleIcon sx={{ fontSize: 20, color: "white" }} />
              </div>
              <div className="flex-col-el flex-1">
                <div className="text-[24px] text-textcolor-black">
                  {i18n.t("MSG_PARENT_PAGE_APPLE_ID")}
                </div>
                {apple && (
                  <div className="text-[18px] text-gray2-100">
                    {
                      parentsAuth.currentUser?.providerData
                        .filter(
                          (provider) => provider.providerId === "apple.com",
                        )
                        .shift()?.email
                    }
                  </div>
                )}
              </div>
              {apple ? (
                <button
                  onClick={handleLinkApple}
                  className="ml-auto bg-transparent border-2 border-textcolor-black text-textcolor-black rounded-full py-2 px-4 text-[21px] cursor-pointer hover:bg-gray2-100/10 transition-all"
                >
                  {i18n.t("MSG_PARENT_PAGE_UNLINK")}
                </button>
              ) : (
                <button
                  onClick={handleLinkApple}
                  className="ml-auto bg-white border border-gray2-60 text-textcolor-black rounded-full py-2 px-4 text-[21px] cursor-pointer hover:bg-gray2-20 transition-all"
                >
                  {i18n.t("MSG_PARENT_PAGE_LINK")}
                </button>
              )}
            </div>
          )}
        </div>
      )}

      {/* アカウント操作 */}
      <div className="bg-white rounded-[16px] p-6">
        <div className="flex-row-el items-center gap-2 text-[27px] text-textcolor-black mb-5 pb-3 border-b border-outline">
          <span className="material-symbols-outlined !text-[45px] text-red-100">
            settings
          </span>
          {i18n.t("MSG_PARENT_PAGE_ACCOUNT_ACTIONS")}
        </div>

        <div className="flex-col-el gap-3 mt-4">
          <button
            className="flex-row-el items-center justify-center gap-2 w-full py-3.5 px-7 bg-blue-100 text-white rounded-[12px] text-[24px] border-none cursor-pointer hover:opacity-90 transition-all"
            onClick={handleOpenQrLogin}
          >
            <span className="material-symbols-outlined">qr_code</span>
            <span className={i18n.locale === "en" ? "text-[18px]" : ""}>
              {i18n.t("MSG_QR_LOGIN_ISSUE_BTN")}
            </span>
          </button>
          {parent.provider === "password" && (
            <button
              className="flex-row-el items-center justify-center gap-2 w-full py-3.5 px-7 bg-blue-100 text-white rounded-[12px] text-[24px] border-none cursor-pointer hover:opacity-90 transition-all"
              onClick={handleChangePassword}
            >
              <span className="material-symbols-outlined">lock</span>
              {i18n.t("MSG_PARENT_PAGE_CHANGE_PASSWORD")}
            </button>
          )}
          <button
            className="flex-row-el items-center justify-center gap-2 w-full py-3.5 px-7 bg-danger text-white rounded-[12px] text-[24px] border-none cursor-pointer hover:opacity-90 transition-all"
            onClick={handleOpenLogout}
          >
            <span className="material-symbols-outlined">logout</span>
            {i18n.t("MSG_PARENT_PAGE_LOGOUT")}
          </button>
        </div>

        <div className="flex-row-center w-full mt-5">
          <p className="text-body text-font-size-body2 text-textcolor-black text-center">
            {i18n.t("MSG_SETTING_PARENTS_USER_UNSUBSCRIBE_DESC_PREFIX")}
            <span
              onClick={handleDeleteAccount}
              className="text-blue-600 underline hover:text-blue-800 cursor-pointer"
            >
              {i18n.t("MSG_SETTING_PARENTS_USER_UNSUBSCRIBE_CLICK_HERE")}
            </span>
            {i18n.t("MSG_SETTING_PARENTS_USER_UNSUBSCRIBE_DESC_SUFFIX")}
          </p>
        </div>
      </div>

      {nicknameModalOpen && (
        <NicknameEditModal
          open={nicknameModalOpen}
          currentName={parent.display_name || ""}
          onClose={() => setNicknameModalOpen(false)}
          onConfirm={handleNicknameConfirm}
        />
      )}

      {qrLoginOpen && (
        <QrLoginApproveModal
          open={qrLoginOpen}
          onClose={() => setQrLoginOpen(false)}
        />
      )}

      <ParentConfirmModal
        open={signOutConfirm}
        title={i18n.t("MSG_SETTING_PARENTS_USER_SIGN_OUT_TITLE")}
        message={i18n.t("MSG_SETTING_PARENTS_USER_SIGN_OUT_MESSAGE")}
        icon="logout"
        onClose={handleCloseLogoutConfirm}
        onConfirm={handleLogout}
      />

      {deleteAccountOpen && (
        <DeleteAccountModal
          open={deleteAccountOpen}
          onClose={() => setDeleteAccountOpen(false)}
        />
      )}

      {changePasswordOpen && (
        <ChangePasswordModal
          open={changePasswordOpen}
          onClose={() => setChangePasswordOpen(false)}
        />
      )}

      {iconModalOpen && (
        <ParentIconEditModal
          open={iconModalOpen}
          currentIcon={parent.icon}
          parentUid={parent.uid}
          onClose={() => setIconModalOpen(false)}
          onSave={(iconUrl) => {
            dispatch(
              updateAccount({
                uid: parent.uid,
                userInfo: { icon: iconUrl },
              }),
            );
          }}
        />
      )}

      {/* 素材モーダル */}
      {selectedAsset && (
        <AssetPreviewModal
          asset={selectedAsset}
          onClose={() => setSelectedAsset(null)}
          onDelete={handleDeleteAsset}
        />
      )}

      {assetListOpen && (
        <AssetListModal
          assets={assets}
          initialTabs={selectedAssetTabs}
          onClose={() => setAssetListOpen(false)}
          onSelectAsset={(asset) => {
            setAssetListOpen(false);
            setSelectedAsset(asset);
          }}
          showSketchbook={false}
        />
      )}

      {linkError && (
        <WarningMessageBox
          zIndex={999999}
          title={i18n.t(
            "MSG_SETTING_PARENTS_USER_PROFILE_ACCOUNT_LINK_ALREADY_ERROR_TITLE",
          )}
          message={i18n.t(
            "MSG_SETTING_PARENTS_USER_PROFILE_ACCOUNT_LINK_ALREADY_ERROR_MESSAGE",
          )}
          close={handleCloseLinkError}
        />
      )}
    </div>
  );
};
